import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, ArrowRight, Lock } from "lucide-react";

import { auth, setToken } from "@/lib/api";
import Brandmark from "@/components/Brandmark";

const HERO_URL =
  "https://images.unsplash.com/photo-1638555063519-d009e6f3b28b?auto=format&fit=crop&w=1800&q=80";

export default function Login() {
  const navigate = useNavigate();
  // No hardcoded credentials in the shipped bundle — operators type their own.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await auth.login(email, password);
      setToken(res.token);
      toast.success(`Welcome, ${res.user.name || res.user.email}`);
      navigate("/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg ?? "Invalid credentials") : "Invalid credentials";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="noise-overlay min-h-screen grid grid-cols-1 lg:grid-cols-2"
      style={{ background: "rgb(var(--bg))" }}
    >
      {/* LEFT — auth form */}
      <div className="flex flex-col p-8 md:p-12 lg:p-16">
        <Brandmark />

        <div className="flex-1 flex items-center">
          <form
            onSubmit={onSubmit}
            className="w-full max-w-md"
            data-testid="login-form"
          >
            <div className="pj-label mb-4" data-testid="login-eyebrow">
              · Secure Access · PJ1 OS
            </div>
            <h1
              className="font-display mb-3"
              style={{ fontSize: "clamp(30px, 7vw, 44px)", lineHeight: 1.05, letterSpacing: "-0.03em" }}
            >
              The operating system
              <br />
              for private aviation.
            </h1>
            <p
              style={{
                color: "rgb(var(--dim))",
                fontSize: 14,
                maxWidth: 380,
                marginBottom: 36,
              }}
            >
              Ops, concierge, finance & admin sign in here. Public visitors hit the
              widget on the marketing site.
            </p>

            <label className="block mb-4">
              <span className="pj-label block mb-1.5">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                className="w-full px-3 py-3 mono"
                style={{
                  background: "rgb(var(--surface))",
                  border: "1px solid rgb(var(--ln))",
                  color: "rgb(var(--fg))",
                  fontSize: 13,
                }}
              />
            </label>

            <label className="block mb-6">
              <span className="pj-label block mb-1.5">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
                className="w-full px-3 py-3 mono"
                style={{
                  background: "rgb(var(--surface))",
                  border: "1px solid rgb(var(--ln))",
                  color: "rgb(var(--fg))",
                  fontSize: 13,
                }}
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full flex items-center justify-between px-4 py-3 mono uppercase tracking-[0.14em] text-[11px] hover:opacity-90 disabled:opacity-50"
              style={{ borderRadius: 1, background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
            >
              {busy ? "Authenticating…" : "Enter Control Room"}
              <ArrowRight size={14} />
            </button>

            <div className="mt-6 text-[11px] mono" style={{ color: "rgb(var(--dim))" }}>
              <Lock size={11} className="inline mr-1.5" />
              Seeded accounts: admin / ops / finance — see{" "}
              <code className="text-[rgb(var(--fg))]">test_credentials.md</code>.
            </div>
          </form>
        </div>

        <div className="pj-label mt-8" data-testid="login-footnote">
          © Charter Jet One · Private Jet One subsidiary
        </div>
      </div>

      {/* RIGHT — bleed image */}
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(244,239,230,0.12) 0%, rgba(38,31,24,0.62) 100%), url(${HERO_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          borderLeft: "1px solid rgb(var(--ln))",
        }}
      >
        <div className="absolute bottom-10 left-10 right-10" style={{ color: "rgb(252,249,243)" }}>
          <div className="pj-label mb-3" style={{ color: "rgb(224,196,120)" }}>Now operating</div>
          <div
            className="font-display"
            style={{ fontSize: 28, lineHeight: 1.08, letterSpacing: "-0.02em", color: "rgb(252,249,243)" }}
          >
            Source. Price. Book. Fly.
            <br />
            All under 60 seconds.
          </div>
          <div className="mt-4 flex items-center gap-3 mono text-[10px]" style={{ color: "rgba(252,249,243,0.85)", letterSpacing: "0.16em", textTransform: "uppercase" }}>
            <Plane size={12} /> 14 CFR Part 295 broker · operator identity stays off-stage
          </div>
        </div>
      </div>
    </div>
  );
}
